import { useEffect, useRef } from "react";

const editable = (el) => el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);

// Operator shortcuts. Letter keys are delayed and only armed when not part of a reader burst,
// so a tag ID typed by the NFC reader never triggers them.
export const useOperatorKeys = ({ onEnter, onEscape, onMenu, onFullscreen, onReplay, onUnlock, enabled = true }) => {
  const handlers = useRef({});
  handlers.current = { onEnter, onEscape, onMenu, onFullscreen, onReplay, onUnlock };

  useEffect(() => {
    if (!enabled) return undefined;
    let pending = null;
    let lastKeyAt = 0;
    const cancelPending = () => { if (pending) { clearTimeout(pending); pending = null; } };
    const later = (fn) => {
      cancelPending();
      pending = setTimeout(() => { pending = null; window.dispatchEvent(new Event("reader-clear")); fn(); }, 280);
    };

    const onKey = (e) => {
      const h = handlers.current;
      const target = e.target;
      const inReader = editable(target) && target.dataset?.testid === "nfc-scanner-input";
      if (e.key === "Escape") { cancelPending(); h.onEscape?.(); return; }
      if (e.key === "`") { e.preventDefault(); cancelPending(); h.onMenu?.(); return; }
      if (editable(target) && !inReader) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const sinceLast = e.timeStamp - lastKeyAt;
      lastKeyAt = e.timeStamp;
      if (e.key === "Enter" || e.key === "Tab") {
        cancelPending();
        if (e.key === "Tab" || (inReader && target.value)) return;
        h.onEnter?.();
        return;
      }
      const key = e.key.toLowerCase();
      const lone = sinceLast > 150;
      if (key === "f" && lone) { later(() => h.onFullscreen?.()); return; }
      if (key === "r" && lone) { later(() => h.onReplay?.()); return; }
      if (key === "u" && lone) { later(() => h.onUnlock?.()); return; }
      if (e.key.length === 1) cancelPending();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("reader-submit", cancelPending);
    return () => {
      cancelPending();
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("reader-submit", cancelPending);
    };
  }, [enabled]);
};
